'use client'

import { fetchDflowSearch } from '@/lib/api/dflow'
import type { LimitlessMarket } from '@/lib/api/limitless'
import { fetchPolymarketSearch } from '@/lib/api/polymarket'
import type { Platform } from '@/types/market'
import { getEventUrl } from '@/utils/routes'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'

// Limitless search goes through our own API route because limitless.exchange
// doesn't expose CORS headers for browser-origin requests.
async function fetchLimitlessSearchViaProxy(query: string, limit = 10): Promise<LimitlessMarket[]> {
	const res = await fetch(`/api/limitless/search?query=${encodeURIComponent(query)}&limit=${limit}`)
	if (!res.ok) return []
	const data = (await res.json()) as { markets?: LimitlessMarket[] }
	return data.markets ?? []
}

export type SearchResultItem = {
	type: 'event' | 'market'
	id: string
	platform: Platform
	title: string
	subtitle?: string
	imageUrl?: string
	volume?: number
	liquidity?: number
	marketCount?: number
	yesPrice?: number
	noPrice?: number
	// For navigation
	eventId: string
	marketId?: string
}

export type RecentSearchItem = {
	type: 'event' | 'market'
	id: string
	platform: Platform
	title: string
	imageUrl?: string
	eventId: string
	marketId?: string
}

const RECENT_SEARCHES_KEY = 'chance-recent-searches'
const MAX_RECENT_SEARCHES = 5

function getRecentSearches(): RecentSearchItem[] {
	if (typeof window === 'undefined') return []
	try {
		const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
		return stored ? JSON.parse(stored) : []
	} catch {
		return []
	}
}

function saveRecentSearch(item: RecentSearchItem) {
	if (typeof window === 'undefined') return
	try {
		const existing = getRecentSearches()
		const filtered = existing.filter(r => !(r.id === item.id && r.platform === item.platform))
		const updated = [item, ...filtered].slice(0, MAX_RECENT_SEARCHES)
		localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
	} catch {
		// Ignore localStorage errors
	}
}

function getPlatformRoute(platform: Platform, eventId: string, marketId?: string): string {
	const base = getEventUrl(platform, eventId)
	return marketId ? `${base}?marketId=${marketId}` : base
}

export function useCommandPalette(isOpen: boolean, onClose: () => void) {
	const router = useRouter()
	const [query, setQuery] = useState('')
	const [results, setResults] = useState<SearchResultItem[]>([])
	const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([])
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [isLoading, setIsLoading] = useState(false)
	const debounceRef = useRef<NodeJS.Timeout | null>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	// Load recent searches on mount
	useEffect(() => {
		setRecentSearches(getRecentSearches())
	}, [])

	// Reset state when opening
	useEffect(() => {
		if (isOpen) {
			setQuery('')
			setResults([])
			setSelectedIndex(0)
			setRecentSearches(getRecentSearches())
			// Focus input after a small delay to ensure dialog is mounted
			setTimeout(() => inputRef.current?.focus(), 50)
		}
	}, [isOpen])

	// Debounced search
	useEffect(() => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
		}

		if (!query.trim()) {
			setResults([])
			setSelectedIndex(0)
			return
		}

		debounceRef.current = setTimeout(async () => {
			setIsLoading(true)
			try {
				const [polyResults, kalshiResults, limitlessResults] = await Promise.all([
					fetchPolymarketSearch(query, { limit: 10 }).catch(() => ({ events: [] })),
					fetchDflowSearch({ q: query, limit: 10, withNestedMarkets: true }).catch(() => ({
						events: [],
					})),
					fetchLimitlessSearchViaProxy(query, 10).catch(() => []),
				])

				const items: SearchResultItem[] = []

				// Process Polymarket events
				for (const event of polyResults.events ?? []) {
					items.push({
						type: 'event',
						id: event.slug,
						platform: 'polymarket',
						title: event.title,
						subtitle: event.description?.slice(0, 100),
						imageUrl: event.image,
						volume: event.volume,
						liquidity: event.liquidity,
						marketCount: event.markets?.length ?? 0,
						eventId: event.slug,
					})

					// Add top 3 markets from each event
					for (const market of (event.markets ?? []).slice(0, 3)) {
						const prices = market.outcomePrices
							? Array.isArray(market.outcomePrices)
								? market.outcomePrices
								: JSON.parse(market.outcomePrices)
							: [0.5, 0.5]
						items.push({
							type: 'market',
							id: market.id,
							platform: 'polymarket',
							title: market.question,
							subtitle: market.groupItemTitle,
							imageUrl: market.image || event.image,
							volume: market.volumeNum,
							liquidity: market.liquidityNum,
							yesPrice: Number.parseFloat(prices[0]) || 0.5,
							noPrice: Number.parseFloat(prices[1]) || 0.5,
							eventId: event.slug,
							marketId: market.id,
						})
					}
				}

				// Process Limitless markets (flat list — Limitless has no nested events)
				for (const market of limitlessResults ?? []) {
					const rawPrices = market.prices ?? [0.5, 0.5]
					const yesPrice = rawPrices[0] > 1 ? rawPrices[0] / 100 : rawPrices[0]
					const noPrice = rawPrices[1] > 1 ? rawPrices[1] / 100 : rawPrices[1]
					const volumeNum = Number.parseFloat(market.volumeFormatted ?? '0') || 0
					items.push({
						type: 'market',
						id: market.slug,
						platform: 'limitless',
						title: market.title,
						imageUrl: market.imageUrl ?? market.logo ?? undefined,
						volume: volumeNum,
						yesPrice,
						noPrice,
						eventId: market.slug,
					})
				}

				// Process Kalshi events
				for (const event of kalshiResults.events ?? []) {
					const eventTicker = event.ticker.toUpperCase()
					items.push({
						type: 'event',
						id: eventTicker,
						platform: 'kalshi',
						title: event.title,
						subtitle: event.subtitle,
						imageUrl: event.imageUrl,
						volume: event.volume,
						liquidity: event.liquidity,
						marketCount: event.markets?.length ?? 0,
						eventId: eventTicker,
					})

					// Add top 3 markets from each event
					for (const market of (event.markets ?? []).slice(0, 3)) {
						const marketTicker = market.ticker.toUpperCase()
						const yesPrice = market.yesAsk ? Number.parseFloat(market.yesAsk) / 100 : 0.5
						const noPrice = market.noAsk ? Number.parseFloat(market.noAsk) / 100 : 0.5
						items.push({
							type: 'market',
							id: marketTicker,
							platform: 'kalshi',
							title: market.title,
							subtitle: market.subtitle,
							imageUrl: event.imageUrl,
							volume: market.volume,
							yesPrice,
							noPrice,
							eventId: eventTicker,
							marketId: marketTicker,
						})
					}
				}

				setResults(items)
				setSelectedIndex(0)
			} catch (error) {
				console.error('Search error:', error)
				setResults([])
			} finally {
				setIsLoading(false)
			}
		}, 200)

		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current)
			}
		}
	}, [query])

	// Get items to display (results or recent searches)
	const displayItems = query.trim() ? results : recentSearches
	const showRecent = !query.trim() && recentSearches.length > 0

	// Group results by type
	const groupedResults = query.trim()
		? {
				events: results.filter(r => r.type === 'event'),
				markets: results.filter(r => r.type === 'market'),
			}
		: null

	// Navigate to selected item
	const navigateToItem = useCallback(
		(item: SearchResultItem | RecentSearchItem) => {
			const route = getPlatformRoute(item.platform, item.eventId, item.marketId)

			// Save to recent searches
			saveRecentSearch({
				type: item.type,
				id: item.id,
				platform: item.platform,
				title: item.title,
				imageUrl: 'imageUrl' in item ? item.imageUrl : undefined,
				eventId: item.eventId,
				marketId: item.marketId,
			})

			onClose()
			router.push(route)
		},
		[router, onClose]
	)

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			const itemCount = displayItems.length

			switch (e.key) {
				case 'ArrowDown': {
					e.preventDefault()
					setSelectedIndex(prev => (prev + 1) % Math.max(1, itemCount))
					break
				}
				case 'ArrowUp': {
					e.preventDefault()
					setSelectedIndex(prev => (prev - 1 + Math.max(1, itemCount)) % Math.max(1, itemCount))
					break
				}
				case 'Enter': {
					e.preventDefault()
					if (displayItems[selectedIndex]) {
						navigateToItem(displayItems[selectedIndex])
					}
					break
				}
				case 'Escape': {
					e.preventDefault()
					onClose()
					break
				}
			}
		},
		[displayItems, selectedIndex, navigateToItem, onClose]
	)

	return {
		query,
		setQuery,
		results,
		groupedResults,
		recentSearches,
		showRecent,
		displayItems,
		selectedIndex,
		setSelectedIndex,
		isLoading,
		inputRef,
		handleKeyDown,
		navigateToItem,
	}
}
